/**
 * Shared shell for Paxley legal pages (Privacy, Terms, Refund, Cookie).
 *
 * Sits on top of `MarketingLayout`, so the legal surface inherits the warm
 * editorial canvas, nav, and footer. The legal-specific bits (badge, serif
 * title with an italic tail, last-updated line, cross-nav between legal
 * pages) wrap the body.
 *
 * Each top-level child is wrapped in a scroll-triggered Reveal so long
 * policy sections rise in as the reader scrolls rather than dumping a
 * 300-line wall on first paint.
 */
import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "./marketing/MarketingLayout";
import { Em, Eyebrow, Headline, Reveal, Section } from "./marketing/primitives";

export type LegalPageId = "terms" | "privacy" | "refund" | "cookies";

export interface LegalPageLayoutProps {
  badge: string;
  title: string;
  subtitle?: string;
  lastUpdated: string;
  effectiveDate?: string;
  children: React.ReactNode;
  currentPage?: LegalPageId;
}

const LEGAL_LINKS: Array<{ key: LegalPageId; to: string; label: string }> = [
  { key: "privacy", to: "/privacy", label: "Privacy Policy" },
  { key: "terms", to: "/terms", label: "Terms & Conditions" },
  { key: "refund", to: "/refund", label: "Refund Policy" },
  { key: "cookies", to: "/cookies", label: "Cookie Policy" },
];

function splitHeading(title: string): { head: string; tail: string } {
  const trimmed = title.trim();
  const idx = trimmed.lastIndexOf(" ");
  if (idx === -1) return { head: "", tail: trimmed };
  return { head: trimmed.slice(0, idx), tail: trimmed.slice(idx + 1) };
}

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  badge,
  title,
  subtitle,
  lastUpdated,
  effectiveDate,
  children,
  currentPage,
}) => {
  const { head, tail } = splitHeading(title);

  return (
    <MarketingLayout>
      <Section padding="hero" container="narrow">
        <div className="text-center">
          <Reveal>
            <Eyebrow>{badge}</Eyebrow>
          </Reveal>
          <Reveal delay={0.05}>
            <Headline size="hero" className="mt-6 !text-4xl md:!text-6xl">
              {head ? (
                <>
                  {head} <Em>{tail}</Em>
                </>
              ) : (
                tail
              )}
            </Headline>
          </Reveal>
          {subtitle && (
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg leading-relaxed opacity-70">{subtitle}</p>
            </Reveal>
          )}
          <Reveal delay={0.15}>
            <p className="mt-6 text-xs opacity-50">
              {effectiveDate && (
                <>
                  Effective: {effectiveDate}
                  <span className="mx-2">·</span>
                </>
              )}
              Last updated: {lastUpdated}
            </p>
          </Reveal>
        </div>
      </Section>

      <section className="relative pb-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="space-y-10 leading-relaxed text-[#17150F]/80 [&_h2]:mt-12 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-[#17150F] [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#17150F] [&_p]:leading-relaxed [&_a]:text-[#1E4D38] [&_a]:underline [&_a:hover]:opacity-80 [&_strong]:text-[#17150F] [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1.5">
            {React.Children.map(children, (child, idx) =>
              React.isValidElement(child) ? <Reveal key={idx}>{child}</Reveal> : child
            )}
          </div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="mx-auto max-w-3xl px-6">
          <div className="rounded-2xl border border-[#E4DFD3] bg-white p-6">
            <h3 className="mb-4 text-xs font-semibold uppercase tracking-widest opacity-50">
              Other legal documents
            </h3>
            <div className="flex flex-wrap gap-2">
              {LEGAL_LINKS.map((link) => (
                <Link
                  key={link.key}
                  to={link.to}
                  className={`rounded-xl border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    currentPage === link.key
                      ? "border-[#1E4D38]/40 bg-[#E4EFE4] text-[#1E4D38]"
                      : "border-[#E4DFD3] bg-[#FBF8F1] opacity-80 hover:opacity-100"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default LegalPageLayout;
